import { z } from "zod";

/**
 * A content status enumeration for moderation workflow
 * Zod schema for the "ContentStatus" union
 */
export const ContentStatusSchema=z.enum(["pending","approved","denied"]).describe("A content status enumeration for moderation workflow");

/**
 * Zod schema for the "UserRole" union
 */
export const UserRoleSchema=z.enum(["admin","guest"]);

/**
 * Zod schema for the "Account" interface
 * @table account
 * @schema public
 */
export const AccountSchema=z.object({
    id:z.string().describe("Unique id of the account"),
    created_at:z.string().describe("When the account was created"),
    name:z.string().describe("Display name of the account"),
    logo_image_path:z.string().optional().describe("Path to the account's logo image in the 'accounts' storage bucket"),
    hero_image_path:z.string().optional().describe("Path to the account's hero image in the 'accounts' storage bucket"),
    is_default:z.boolean(),
}).describe("An account (tenant) within the system");

/**
 * Zod schema for the "Account_insert" interface
 * @insertFor Account
 * @table account
 * @schema public
 */
export const Account_insertSchema=z.object({
    id:z.string().optional(),
    created_at:z.string().optional(),
    name:z.string(),
    logo_image_path:z.string().optional(),
    hero_image_path:z.string().optional(),
    is_default:z.boolean().optional(),
});

/**
 * Zod schema for the "AccountInvite" interface
 * @table account_invite
 * @schema public
 */
export const AccountInviteSchema=z.object({
    id:z.string().describe("Unique id of the account invite"),
    created_at:z.string().describe("When the invite was created"),
    account_id:z.string().describe("The account the invite belongs to"),
    invited_email:z.string().describe("The email address being invited"),
    role:z.string().describe("Proposed role of the invited user within the account"),
    invite_code:z.string().describe("Unique invite code used to accept or decline the invite"),
    invited_by_user_id:z.string().optional().describe("The user who created the invite"),
    expires_at:z.string().optional().describe("When this invite expires (if set)"),
    accepted_at:z.string().optional().describe("When the invite was accepted (if accepted)"),
    accepted_by_user_id:z.string().optional().describe("The user who accepted the invite (if accepted)"),
}).describe("An invitation to join an account (used for accept-invite flow)");

/**
 * Zod schema for the "AccountInvite_insert" interface
 * @insertFor AccountInvite
 * @table account_invite
 * @schema public
 */
export const AccountInvite_insertSchema=z.object({
    id:z.string().optional(),
    created_at:z.string().optional(),
    account_id:z.string(),
    invited_email:z.string(),
    role:z.string().optional(),
    invite_code:z.string(),
    invited_by_user_id:z.string().optional(),
    expires_at:z.string().optional(),
    accepted_at:z.string().optional(),
    accepted_by_user_id:z.string().optional(),
});

/**
 * Zod schema for the "AccountMembership" interface
 * @table account_membership
 * @schema public
 */
export const AccountMembershipSchema=z.object({
    id:z.string().describe("Unique id of the account membership"),
    created_at:z.string().describe("When the membership was created"),
    last_accessed_at:z.string().describe("The last time the user accessed this account"),
    user_id:z.string().describe("The user who is a member of the account"),
    account_id:z.string().describe("The account the user belongs to"),
    role:z.string().describe("The role of the user within the account"),
}).describe("Links a user to an account with a role");

/**
 * Zod schema for the "AccountMembership_insert" interface
 * @insertFor AccountMembership
 * @table account_membership
 * @schema public
 */
export const AccountMembership_insertSchema=z.object({
    id:z.string().optional(),
    created_at:z.string().optional(),
    last_accessed_at:z.string().optional(),
    user_id:z.string(),
    account_id:z.string(),
    role:z.string().optional(),
});

/**
 * Zod schema for the "FormSubmission" interface
 * @table form_submission
 * @schema public
 */
export const FormSubmissionSchema=z.object({
    id:z.string(),
    created_at:z.string(),
    data:z.record(z.string(),z.any()),
    name:z.string(),
});

/**
 * Zod schema for the "FormSubmission_insert" interface
 * @insertFor FormSubmission
 * @table form_submission
 * @schema public
 */
export const FormSubmission_insertSchema=z.object({
    id:z.string().optional(),
    created_at:z.string().optional(),
    data:z.record(z.string(),z.any()),
    name:z.string().optional(),
});

/**
 * Zod schema for the "Guardian" interface
 * @table guardian
 * @schema public
 */
export const GuardianSchema=z.object({
    id:z.string().describe("Unique id of the guardian"),
    created_at:z.string().describe("When the guardian was created"),
    account_id:z.string().describe("Owning account of this guardian"),
    created_by_user_id:z.string().optional().describe("The user who created this guardian (if available)"),
    name:z.string().describe("Display name of the guardian"),
    status:z.string().describe("Moderation status of the guardian"),
    description:z.string().optional().describe("Short description of the guardian"),
    long_description:z.string().optional().describe("Long-form markdown description of the guardian"),
    image_path:z.string().optional().describe("Primary image path for the guardian"),
    video_path:z.string().optional().describe("Optional video path for the guardian"),
    tags:z.string().array().describe("Tags used for discovery and related content"),
    social_links:z.string().array().describe("Social links (URLs) associated with the guardian"),
    image_paths:z.string().array().describe("Additional image paths for media gallery"),
    is_public:z.boolean().describe("Whether the guardian is visible across accounts"),
    is_featured:z.boolean().describe("Whether the guardian should be featured on the landing page"),
    what_they_love:z.string().optional().describe("what they love"),
}).describe("A person (content-creating Guardian) who leads or represents initiatives");

/**
 * Zod schema for the "Guardian_insert" interface
 * @insertFor Guardian
 * @table guardian
 * @schema public
 */
export const Guardian_insertSchema=z.object({
    id:z.string().optional(),
    created_at:z.string().optional(),
    account_id:z.string(),
    created_by_user_id:z.string().optional(),
    name:z.string(),
    status:z.string().optional(),
    description:z.string().optional(),
    long_description:z.string().optional(),
    image_path:z.string().optional(),
    video_path:z.string().optional(),
    tags:z.string().optional().array(),
    social_links:z.string().optional().array(),
    image_paths:z.string().optional().array(),
    is_public:z.boolean().optional(),
    is_featured:z.boolean().optional(),
    what_they_love:z.string().optional(),
});

/**
 * Zod schema for the "Project" interface
 * @table project
 * @schema public
 */
export const ProjectSchema=z.object({
    id:z.string().describe("Unique id of the project"),
    created_at:z.string().describe("When the project was created"),
    account_id:z.string().describe("Owning account of this project"),
    created_by_user_id:z.string().optional().describe("The user who created this project (if available)"),
    guardian_id:z.string().describe("The guardian who owns this project"),
    name:z.string().describe("Display name of the project"),
    status:z.string().describe("Moderation status of the project"),
    slogan:z.string().optional().describe("Short slogan/value statement of the project"),
    description:z.string().optional().describe("Short description of the project"),
    long_description:z.string().optional().describe("Long-form markdown description of the project"),
    tags:z.string().array().describe("Tags used for discovery and related content"),
    lat:z.number().optional().describe("Latitude of the project's location"),
    lng:z.number().optional().describe("Longitude of the project's location"),
    image_path:z.string().optional().describe("Primary image path for the project"),
    video_path:z.string().optional().describe("Optional video path for the project"),
    social_links:z.string().array().describe("Social links (URLs) associated with the project"),
    website_url:z.string().optional().describe("Project website URL"),
    needs:z.string().array().describe("Project needs (actionable items)"),
    image_paths:z.string().array().describe("Additional image paths for media gallery"),
    is_public:z.boolean().describe("Whether the project is visible across accounts"),
    is_featured:z.boolean().describe("Whether the project should be featured on the landing page"),
}).describe("A project (initiative) associated with a guardian");

/**
 * Zod schema for the "Project_insert" interface
 * @insertFor Project
 * @table project
 * @schema public
 */
export const Project_insertSchema=z.object({
    id:z.string().optional(),
    created_at:z.string().optional(),
    account_id:z.string(),
    created_by_user_id:z.string().optional(),
    guardian_id:z.string(),
    name:z.string(),
    status:z.string().optional(),
    slogan:z.string().optional(),
    description:z.string().optional(),
    long_description:z.string().optional(),
    tags:z.string().optional().array(),
    lat:z.number().optional(),
    lng:z.number().optional(),
    image_path:z.string().optional(),
    video_path:z.string().optional(),
    social_links:z.string().optional().array(),
    website_url:z.string().optional(),
    needs:z.string().optional().array(),
    image_paths:z.string().optional().array(),
    is_public:z.boolean().optional(),
    is_featured:z.boolean().optional(),
});

/**
 * Zod schema for the "User" interface
 * @table user
 * @schema public
 */
export const UserSchema=z.object({
    id:z.string().describe("Unique id of the user"),
    created_at:z.string().describe("When the user was created"),
    name:z.string().describe("Full name of the user"),
    email:z.string().describe("Email address of the user"),
    profile_image_path:z.string().optional().describe("Path to the user's profile image in the 'accounts' storage bucket"),
    hero_image_path:z.string().optional().describe("Path to the user's hero image in the 'accounts' storage bucket"),
}).describe("A user");

/**
 * Zod schema for the "User_insert" interface
 * @insertFor User
 * @table user
 * @schema public
 */
export const User_insertSchema=z.object({
    id:z.string().optional(),
    created_at:z.string().optional(),
    name:z.string(),
    email:z.string(),
    profile_image_path:z.string().optional(),
    hero_image_path:z.string().optional(),
});